<script setup lang="ts">
import { Preferences } from '@capacitor/preferences'
import { useAuthStore } from '~/store/authStore'
import { checkPermissions, getToken, initializeFirebase, requestPermission } from '~/services/notifications'

const authStore = useAuthStore()
const user = await authStore.getUser()
const { $api } = useNuxtApp()

const canAskPermission = ref(false)
const fcmToken = ref<string | null>(null)
const pushEnabled = ref(false)

watch(canAskPermission, (newValue) => {
  Preferences.set({ key: 'canAskPermission', value: newValue.toString() })
})

initializeFirebase()

onMounted(() => {
  if (authStore.didSaveNotifications) {
    return
  }
  checkPermissions().then((result) => {
    if (result === 'granted') {
      pushEnabled.value = true
      getToken().then((token) => {
        fcmToken.value = token
      })
    }
  })
})

onMounted(async () => {
  if (authStore.didSaveNotifications) {
    return
  }
  checkPermissions().then(async (result) => {
    if (result === 'granted') {
      subscribe()
      authStore.didSaveNotifications = true
    }
    if (result === 'prompt') {
      const { value } = await Preferences.get({ key: 'canAskPermission' })
      canAskPermission.value = value !== 'false'
      authStore.didSaveNotifications = true
    }
  })
})

function subscribe(notify: boolean = false) {
  if (!authStore.user?.id) {
    console.error('Cannot subscribe to notification. Not logged in.')
    return
  }

  pushLoading.value = true
  requestPermission().then((value) => {
    if (value.receive == 'granted') {
      getToken().then((token) => {
        $api('/app/save-subscription', {
          method: 'POST',
          body: {
            token,
          },
        })
          .then(function () {
            pushEnabled.value = true
            pushLoading.value = false
            canAskPermission.value = false
            if (notify) {
              useSonner.success('Push notifications enabled')
            }
          })
      })
        .finally(() => {
          pushLoading.value = false
        })
    }
    if (value.receive == 'denied') {
      canAskPermission.value = false
    }
  }).finally(() => {
    pushLoading.value = false
  })
}

const markNotificationAsRead = async (notification: DSNotification) => {
  await $api('/notifications/mark-as-read', {
    method: 'POST',
    body: { id: notification.id },
  })
  await authStore.loadFreshNotifications()
}

const markAllNotificationsAsRead = async () => {
  await $api('/notifications/mark-all-as-read', {
    method: 'POST',
  })
  await authStore.loadFreshNotifications()
}

const pushLoading = ref(false)
</script>

<template>
  <div
    v-if="user"
    class="flex flex-col gap-2"
  >
    <UiAlertDialog
      v-model:open="canAskPermission"
      @close="canAskPermission = false"
    >
      <UiAlertDialogContent>
        <UiAlertDialogHeader>
          <UiAlertDialogTitle>{{ $t('Enable push notifications') }}</UiAlertDialogTitle>
          <UiAlertDialogDescription>
            <strong>{{ $t('Do you want to stay up to date with your tournaments?') }} </strong>
            <br>
            {{
              $t('If you enable push notifications, you will get notified when your registration is complete, you need to play or write and other tournament related updates')
            }}
          </UiAlertDialogDescription>
        </UiAlertDialogHeader>
        <UiAlertDialogFooter>
          <UiAlertDialogCancel
            @click="canAskPermission = false"
          >
            {{ $t('No, thanks') }}
          </UiAlertDialogCancel>
          <UiAlertDialogAction
            @click="() => subscribe(true)"
          >
            {{ $t('Yes, enable push notifications') }}
          </UiAlertDialogAction>
        </UiAlertDialogFooter>
      </UiAlertDialogContent>
    </UiAlertDialog>
    <div class="flex flex-col gap-2">
      <UiButton
        v-if="!pushEnabled"
        :loading="pushLoading"
        icon="tabler:bell"
        class="w-full"
        @click="() => subscribe(true)"
      >
        {{ $t('Enable push notifications') }}
      </UiButton>
      <UiButton
        v-if="authStore.hasUnreadNotifications"
        icon="tabler:bell-off"
        class="w-full"
        @click="markAllNotificationsAsRead"
      >
        {{ $t('Mark all notifications as read') }}
      </UiButton>
    </div>
    <UiSheetClose
      v-for="notification in authStore.notifications"
      :key="notification.id"
      as-child
    >
      <NuxtLink
        v-if="notification.data.action"
        :to="notification.data.action.url.replace(/https?:\/\/dartsspace\.(com|test)/g, '')"
        class="p-3 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer flex items-center gap-3 relative"
        :class="{ 'opacity-50': notification.read_at !== null }"
        @click="markNotificationAsRead(notification)"
      >
        <div class="text-sm flex-1">
          <div class="flex flex-col items-start justify-between">
            <div class="flex flex-row gap-1 items-center">
              <span class="text-gray-900 dark:text-white font-medium">{{ notification.data.title }}</span>
            </div>

            <CommonDateTime
              :date="notification.created_at"
              class="text-gray-500 dark:text-gray-400 text-xs"
            />
          </div>
          <p class="text-gray-500 dark:text-gray-400">
            {{ notification.data.body }}
          </p>
        </div>
      </NuxtLink>
      <div
        v-else-if="notification.data.title"
        class="p-3 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer flex items-center gap-3 relative"
      >
        <div class="text-sm flex-1">
          <div class="flex flex-col items-start justify-between">
            <div class="flex flex-row gap-1 items-center">
              <div
                v-if="notification.read_at === null"
                class="w-2 h-2 bg-red-500 rounded-full"
              />
              <span class="text-gray-900 dark:text-white font-medium">{{ notification.data.title }}</span>
            </div>

            <CommonDateTime
              :date="notification.created_at"
              class="text-gray-500 dark:text-gray-400 text-xs"
            />
          </div>
          <p class="text-gray-500 dark:text-gray-400">
            {{ notification.data.body }}
          </p>
        </div>
      </div>
    </UiSheetClose>
  </div>
</template>
